import React, { useState, useEffect } from "react";
import { Seo } from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { useModals } from "@/context/ModalContext";
import { apiCreateCheckout, apiRedeemPromo } from "@/lib/namable";
import { Check, Sparkles, Loader2, Tag } from "lucide-react";
import { toast } from "sonner";

const PLANS = [
  {
    id: "starter",
    name: "Starter",
    monthly: 9.99,
    yearly: 95.9,
    scans: "100 scans / month",
    features: ["100 name scans per month", ".com search unlocked", "AI estimated domain values", "Up to 25 saved names", "Full search history", "All domain extensions"],
    recommended: false,
  },
  {
    id: "pro",
    name: "Pro",
    monthly: 27.99,
    yearly: 268.7,
    scans: "200 scans / month",
    features: ["200 name scans per month", "Everything in Starter", "Unlimited saved names", "Bulk domain checks", "CSV exports", "Priority AI generation"],
    recommended: true,
  },
];

export default function Pricing() {
  const { user, usage, refreshUsage, track } = useAuth();
  const { openAuth } = useModals();
  const [billing, setBilling] = useState("monthly");
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [promo, setPromo] = useState("");
  const [promoLoading, setPromoLoading] = useState(false);

  useEffect(() => {
    track("page_viewed", { page: "pricing" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startCheckout = async (planId) => {
    setLoadingPlan(planId);
    track("checkout_started", { plan: planId, billing });
    try {
      const { checkout_url } = await apiCreateCheckout({ plan: planId, billing, user_id: user?.user_id });
      window.location.href = checkout_url;
    } catch (e) {
      setLoadingPlan(null);
      toast.error("Couldn't start checkout. Please try again.");
    }
  };

  const choose = (planId) => {
    track("plan_selected", { plan: planId, billing });
    if (!user) {
      openAuth({ mode: "register", title: "Create your account to subscribe", onSuccess: () => setTimeout(() => startCheckout(planId), 200) });
      return;
    }
    startCheckout(planId);
  };

  const applyPromo = async () => {
    if (!promo.trim()) return;
    if (!user) {
      openAuth({ mode: "register", title: "Log in to redeem a code" });
      return;
    }
    setPromoLoading(true);
    try {
      const res = await apiRedeemPromo(promo.trim());
      if (res.type === "comp") {
        toast.success(res.message || "Promo applied — enjoy your upgrade!");
        await refreshUsage();
      } else if (res.type === "offering") {
        toast.success(`Discount code accepted (${res.percent}% off). Pick a plan to continue.`);
      } else {
        toast.success("Promo applied.");
      }
      track("promo_applied", { code: promo.trim() });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid or expired code.");
    } finally {
      setPromoLoading(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
      <Seo title="Pricing — Namable" description="Simple, conversion-focused pricing for Namable. Starter $9.99/mo (100 scans) or Pro $27.99/mo (200 scans)." path="/pricing" />

      <div className="text-center max-w-[560px] mx-auto">
        <p className="overline text-brand">Pricing</p>
        <h1 className="font-display text-[32px] sm:text-[42px] font-extrabold tracking-[-0.025em] text-ink mt-2">
          Simple plans for serious founders
        </h1>
        <p className="text-[15px] text-ink-muted mt-3">Start free. Upgrade when you're ready to claim more names.</p>
      </div>

      {/* billing toggle */}
      <div className="flex justify-center mt-8">
        <div className="inline-flex p-1 bg-canvas-alt rounded-full" data-testid="billing-toggle">
          {["monthly", "yearly"].map((b) => (
            <button
              key={b}
              onClick={() => setBilling(b)}
              data-testid={`billing-${b}`}
              className={`px-5 py-2 rounded-full text-[13px] font-bold capitalize transition-all ${
                billing === b ? "bg-surface text-ink shadow-soft" : "text-ink-muted"
              }`}
            >
              {b} {b === "yearly" && <span className="text-success ml-1">save 20%</span>}
            </button>
          ))}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-5 mt-10 max-w-[760px] mx-auto">
        {PLANS.map((p) => {
          const price = billing === "monthly" ? p.monthly : p.yearly;
          const perMonth = billing === "yearly" ? (p.yearly / 12).toFixed(2) : p.monthly.toFixed(2);
          const isCurrent = usage?.plan === p.id;
          return (
            <div
              key={p.id}
              data-testid={`plan-card-${p.id}`}
              className={`relative rounded-xxl p-6 sm:p-7 transition-all duration-200 ${
                p.recommended ? "panel-dark shadow-card ring-2 ring-brand" : "card-soft"
              }`}
            >
              {p.recommended && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-brand text-white text-[11px] font-bold uppercase tracking-wide px-3 py-1 rounded-full shadow-glow-sm">
                  Recommended
                </span>
              )}
              <h3 className={`font-display text-[20px] font-extrabold ${p.recommended ? "text-white" : "text-ink"}`}>{p.name}</h3>
              <p className={`text-[13px] mt-0.5 ${p.recommended ? "text-white/70" : "text-ink-subtle"}`}>{p.scans}</p>
              <div className="mt-5 flex items-end gap-1">
                <span className={`font-display text-[40px] font-extrabold tracking-[-0.03em] ${p.recommended ? "text-white" : "text-ink"}`}>
                  ${billing === "monthly" ? price.toFixed(2) : perMonth}
                </span>
                <span className={`text-[14px] font-semibold mb-2 ${p.recommended ? "text-white/60" : "text-ink-subtle"}`}>/mo</span>
              </div>
              {billing === "yearly" && (
                <p className={`text-[12px] ${p.recommended ? "text-white/60" : "text-ink-subtle"}`}>Billed ${price.toFixed(2)} yearly</p>
              )}

              <Button
                onClick={() => choose(p.id)}
                disabled={loadingPlan === p.id || isCurrent}
                data-testid={`subscribe-${p.id}`}
                className={`w-full mt-6 h-12 rounded-xl font-bold ${
                  p.recommended ? "bg-brand hover:bg-brand-dark text-white btn-glow" : "bg-brand hover:bg-brand-dark text-white"
                }`}
              >
                {loadingPlan === p.id ? <Loader2 className="animate-spin" size={18} /> : isCurrent ? "Current plan" : `Get ${p.name}`}
              </Button>

              <ul className="mt-6 space-y-2.5">
                {p.features.map((f) => (
                  <li key={f} className={`flex items-start gap-2.5 text-[13.5px] ${p.recommended ? "text-white/90" : "text-ink"}`}>
                    <span className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${p.recommended ? "bg-brand/30" : "bg-success-bg"}`}>
                      <Check size={12} strokeWidth={3} className={p.recommended ? "text-white" : "text-success"} />
                    </span>
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>

      {/* promo */}
      <div className="max-w-[420px] mx-auto mt-10">
        <p className="text-[12px] font-semibold text-ink-muted mb-2 flex items-center gap-1.5">
          <Tag size={13} /> Have a promo code?
        </p>
        <div className="flex gap-2">
          <Input
            value={promo}
            onChange={(e) => setPromo(e.target.value)}
            placeholder="Enter code"
            className="h-11 rounded-lg"
            data-testid="promo-input"
          />
          <Button onClick={applyPromo} disabled={promoLoading} variant="outline" className="h-11 rounded-lg font-semibold px-5" data-testid="promo-apply-btn">
            {promoLoading ? <Loader2 className="animate-spin" size={16} /> : "Apply"}
          </Button>
        </div>
      </div>

      <p className="text-center text-[12px] text-ink-subtle mt-10 flex items-center justify-center gap-1.5">
        <Sparkles size={13} className="text-brand" /> Secure checkout via Stripe · cancel anytime
      </p>
    </div>
  );
}
